// DomainDashboardPOC.js — Documentation Fonctionnelle : POC « Tableau de Bord Domaine » (module Rapports & Supervision).
// Aperçu interactif de l'écran réel, fidèle à sa DISPOSITION :
//   - vue SÉLECTEUR : recherche + cartes des domaines d'audit (type système, environnement,
//     statut actif, nombre de modules) ;
//   - vue DASHBOARD : bandeau du domaine (jauge de score, sessions, anomalies ouvertes,
//     dernière session), tendance des scores (mini-graphique SVG « maison » avec seuils 70/85),
//     performance par module (barres colorées) et liste des modules audités avec leurs stats.
// Graphiques = divs + SVG « maison » (pas de lib). Actions SIMULÉES localement (aucun appel réseau).
// Source de vérité : scanbank-frontend/src/features/[SCANBANK] Reports/[RPT][02]-DomainDashboard.js
// + agrégats serveur GET /api/scanbank/reports/domains/{code}/dashboard (SCANBANK_REPORT_VIEW).
import React, { useState } from 'react';
import {
  Server, Database, Shield, RefreshCw, ArrowLeft, ArrowRight, Activity,
  AlertTriangle, CheckCircle2, Clock, BarChart3, TrendingUp,
  Layers, Search, Info, ListChecks, Calculator, Target, FileText,
} from 'lucide-react';
import { Card, CardContent, Badge, EmptyState } from '../ui';

// ---- Seuils & couleurs de score (règle ScanBank : ACCEPTABLE ≥ 85, ALERTE ≥ 70, CRITIQUE sinon)
const scoreColor = (s) => (s == null ? '#9CA3AF' : s >= 85 ? '#10B981' : s >= 70 ? '#F59E0B' : '#EF4444');
const scoreLevel = (s) => (s == null ? '—' : s >= 85 ? 'ACCEPTABLE' : s >= 70 ? 'ALERTE' : 'CRITIQUE');

// ---- Dictionnaires (types système et environnements de l'écran réel) -----------
const SYSTEM_TYPES = {
  CBS: { label: 'Core Banking', color: '#3B82F6', icon: Database },
  ERP: { label: 'ERP', color: '#8B5CF6', icon: Server },
  RISK_MANAGEMENT: { label: 'Gestion Risques', color: '#F97316', icon: Shield },
  AML: { label: 'AML', color: '#EF4444', icon: AlertTriangle },
  OTHER: { label: 'Autre', color: '#6B7280', icon: Server },
};

const ENVIRONNEMENTS = {
  PROD: { label: 'Production', color: '#EF4444' },
  UAT: { label: 'UAT', color: '#F59E0B' },
  DEV: { label: 'Développement', color: '#3B82F6' },
  PREPROD: { label: 'Pré-Production', color: '#F97316' },
};

// ---- Données en dur : domaines + réponse simulée de /domains/{code}/dashboard ---
// Cohérent avec les POC Dashboard / SessionReports / Supervision (mêmes domaines, scores, sessions).
const DOMAINES = [
  {
    code: 'CBS_AMPLITUDE', name: 'Core Banking Amplitude', systemType: 'CBS', environment: 'PROD',
    version: '11.2', owner: 'DSI Production Bancaire', active: true,
    dashboard: {
      totalSessions: 36, averageConformityScore: 81.7, totalAnomalies: 87, anomaliesOuvertes: 25, critiques: 9,
      lastSessionCode: 'SES_CBS_AMPLITUDE_CREDITS_20260711081420_J9K0L1M2', lastSessionDate: '11/07/2026 08:19', lastConformityScore: 77.0,
    },
    tendance: [
      { session: '19/06', score: 76.2 }, { session: '22/06', score: 79.8 }, { session: '25/06', score: 74.5 },
      { session: '28/06', score: 81.0 }, { session: '01/07', score: 83.6 }, { session: '04/07', score: 72.1 },
      { session: '06/07', score: 85.2 }, { session: '08/07', score: 67.5 }, { session: '10/07', score: 90.0 },
      { session: '11/07', score: 77.0 },
    ],
    modules: [
      { module: 'HABILITATION', totalSessions: 6, scoreMoyen: 68.4, totalAnomalies: 26, ouvertes: 7, derniereSession: '08/07/2026' },
      { module: 'COMPTABILITE', totalSessions: 5, scoreMoyen: 90.2, totalAnomalies: 11, ouvertes: 2, derniereSession: '10/07/2026' },
      { module: 'CREDITS', totalSessions: 5, scoreMoyen: 77.1, totalAnomalies: 15, ouvertes: 5, derniereSession: '11/07/2026' },
      { module: 'DEPOTS', totalSessions: 4, scoreMoyen: 88.0, totalAnomalies: 7, ouvertes: 1, derniereSession: '09/07/2026' },
      { module: 'VIREMENTS', totalSessions: 6, scoreMoyen: 82.6, totalAnomalies: 11, ouvertes: 4, derniereSession: '11/07/2026' },
      { module: 'CLIENTS', totalSessions: 4, scoreMoyen: 86.9, totalAnomalies: 7, ouvertes: 2, derniereSession: '07/07/2026' },
      { module: 'TRESORERIE', totalSessions: 3, scoreMoyen: 91.4, totalAnomalies: 4, ouvertes: 1, derniereSession: '06/07/2026' },
      { module: 'RISQUES', totalSessions: 3, scoreMoyen: 74.8, totalAnomalies: 6, ouvertes: 3, derniereSession: '05/07/2026' },
    ],
  },
  {
    code: 'CBS_BANK_CEMAC', name: 'Core Banking CEMAC', systemType: 'CBS', environment: 'PROD',
    version: '4.8', owner: 'DSI Filiales CEMAC', active: true,
    dashboard: {
      totalSessions: 10, averageConformityScore: 84.2, totalAnomalies: 14, anomaliesOuvertes: 5, critiques: 1,
      lastSessionCode: 'SES_CBS_BANK_CEMAC_VIREMENTS_20260709163055_N3P4Q5R6', lastSessionDate: '09/07/2026 16:33', lastConformityScore: 91.3,
    },
    tendance: [
      { session: '28/06', score: 79.4 }, { session: '01/07', score: 82.0 }, { session: '03/07', score: 72.5 },
      { session: '05/07', score: 85.6 }, { session: '07/07', score: 88.6 }, { session: '09/07', score: 91.3 },
    ],
    modules: [
      { module: 'HABILITATION', totalSessions: 3, scoreMoyen: 72.5, totalAnomalies: 7, ouvertes: 3, derniereSession: '09/07/2026' },
      { module: 'VIREMENTS', totalSessions: 4, scoreMoyen: 91.3, totalAnomalies: 3, ouvertes: 1, derniereSession: '09/07/2026' },
      { module: 'CLIENTS', totalSessions: 3, scoreMoyen: 88.6, totalAnomalies: 4, ouvertes: 1, derniereSession: '04/07/2026' },
    ],
  },
  {
    code: 'AML_TRACKER', name: 'Anti-Blanchiment Tracker', systemType: 'AML', environment: 'UAT',
    version: '2.1', owner: 'Direction Conformité', active: true,
    dashboard: {
      totalSessions: 4, averageConformityScore: 66.8, totalAnomalies: 13, anomaliesOuvertes: 11, critiques: 5,
      lastSessionCode: 'SES_AML_TRACKER_CLIENTS_20260710102244_W1X2Y3Z4', lastSessionDate: '10/07/2026 10:26', lastConformityScore: 64.0,
    },
    tendance: [
      { session: '04/07', score: 71.2 }, { session: '06/07', score: 69.5 }, { session: '08/07', score: 66.4 },
      { session: '10/07', score: 64.0 },
    ],
    modules: [
      { module: 'CLIENTS', totalSessions: 2, scoreMoyen: 64.0, totalAnomalies: 6, ouvertes: 5, derniereSession: '10/07/2026' },
      { module: 'VIREMENTS', totalSessions: 2, scoreMoyen: 69.5, totalAnomalies: 7, ouvertes: 6, derniereSession: '10/07/2026' },
    ],
  },
];

// ---- Sous-composants « maison » ---------------------------------------------------
// Jauge circulaire de conformité (conic-gradient, pas de SVG/lib).
const ScoreGauge = ({ score, size = 96 }) => {
  const color = scoreColor(score);
  return (
    <div className="relative inline-flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      <div className="rounded-full" style={{ width: size, height: size, background: `conic-gradient(${color} ${(score || 0) * 3.6}deg, rgba(156,163,175,0.25) 0deg)` }} />
      <div className="absolute rounded-full bg-white dark:bg-gray-800 flex flex-col items-center justify-center" style={{ width: size - 18, height: size - 18 }}>
        <span className="text-xl font-bold leading-none" style={{ color }}>{score}%</span>
        <span className="text-[9px] font-semibold mt-0.5" style={{ color }}>{scoreLevel(score)}</span>
      </div>
    </div>
  );
};

// Mini-graphique de tendance SVG « maison » : polyligne + points colorés par seuils
// + lignes de seuil 70 (rouge) et 85 (vert) en pointillés.
const TendanceChart = ({ points }) => {
  const W = 560; const H = 170; const PAD = 28;
  const MIN = 50; const MAX = 100;
  const x = (i) => PAD + (i * (W - PAD * 2)) / Math.max(1, points.length - 1);
  const y = (s) => PAD + ((MAX - Math.max(MIN, Math.min(MAX, s))) * (H - PAD * 2)) / (MAX - MIN);
  const chemin = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.score).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Tendance des scores de conformité">
      <line x1={PAD} x2={W - PAD} y1={y(85)} y2={y(85)} stroke="#10B981" strokeDasharray="4 4" strokeWidth="1" />
      <text x={W - PAD + 4} y={y(85) + 3} fontSize="9" fill="#10B981">85</text>
      <line x1={PAD} x2={W - PAD} y1={y(70)} y2={y(70)} stroke="#EF4444" strokeDasharray="4 4" strokeWidth="1" />
      <text x={W - PAD + 4} y={y(70) + 3} fontSize="9" fill="#EF4444">70</text>
      <path d={chemin} fill="none" stroke="#3B82F6" strokeWidth="2" />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={x(i)} cy={y(p.score)} r="4" fill={scoreColor(p.score)}>
            <title>{`${p.session} — ${p.score}%`}</title>
          </circle>
          <text x={x(i)} y={y(p.score) - 8} fontSize="8.5" textAnchor="middle" fill="#6B7280">{p.score}</text>
          <text x={x(i)} y={H - 8} fontSize="9" textAnchor="middle" fill="#9CA3AF">{p.session}</text>
        </g>
      ))}
    </svg>
  );
};

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page ---------------------------------------------------------------------------
const DomainDashboardPOC = () => {
  const [selectedCode, setSelectedCode] = useState(null); // null = vue sélecteur
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');

  const notify = (msg) => { setToast(msg); window.setTimeout(() => setToast(''), 2400); };

  const filteredDomains = DOMAINES.filter((d) =>
    `${d.name} ${d.code} ${d.systemType}`.toLowerCase().includes(search.toLowerCase()));

  const domaine = selectedCode ? DOMAINES.find((d) => d.code === selectedCode) : null;
  const modulesTries = domaine ? [...domaine.modules].sort((a, b) => b.scoreMoyen - a.scoreMoyen) : [];

  return (
    <div className="space-y-8">
      {/* ===== (1) Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tableau de Bord Domaine</h1>
          <Badge color="#10B981">Module Rapports &amp; Supervision</Badge>
          <Badge color="#3B82F6">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Aperçu interactif de l'écran <strong>Tableau de Bord Domaine</strong> : sélection d'un domaine
          d'audit puis vue analytique (agrégats, tendance des scores, performance par module CBS).
        </p>
      </div>

      {/* ===== (2) Aperçu interactif ===== */}
      <Card className="overflow-hidden">
        {/* En-tête héro type app */}
        <div className="px-5 py-4 bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 text-white flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {domaine && (
              <button onClick={() => setSelectedCode(null)} className="p-2 rounded-xl bg-white/15 hover:bg-white/25 transition-colors shrink-0" title="Changer de domaine">
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <BarChart3 className="h-6 w-6 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg font-bold leading-tight truncate">{domaine ? domaine.name : 'Tableau de Bord Domaine'}</h2>
              <p className="text-xs text-white/80 truncate">
                {domaine ? `${domaine.code} • ${SYSTEM_TYPES[domaine.systemType]?.label} • v${domaine.version}` : "Sélectionnez un domaine d'audit pour visualiser son tableau de bord analytique"}
              </p>
            </div>
          </div>
          {domaine ? (
            <div className="flex items-center gap-2 shrink-0">
              <Badge color="#FFFFFF" className="bg-white/20 border-white/30 text-white">{ENVIRONNEMENTS[domaine.environment]?.label || domaine.environment}</Badge>
              <button onClick={() => notify('Agrégats du domaine actualisés (simulation).')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-xs font-medium transition-colors">
                <RefreshCw className="h-3.5 w-3.5" />Actualiser
              </button>
              <button onClick={() => notify(`Navigation vers Rapports de Sessions filtrés sur ${domaine.code} (simulation).`)}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-xs font-medium transition-colors">
                <FileText className="h-3.5 w-3.5" />Rapports
              </button>
            </div>
          ) : (
            <Badge color="#FFFFFF" className="bg-white/20 border-white/30 text-white shrink-0">{filteredDomains.length} domaine(s)</Badge>
          )}
        </div>

        {/* ----- VUE SÉLECTEUR DE DOMAINE ----- */}
        {!domaine && (
          <div className="p-5 space-y-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un domaine…"
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:outline-none" />
            </div>
            {filteredDomains.length === 0 ? (
              <EmptyState icon={Database} title="Aucun domaine" message="Aucun domaine ne correspond à votre recherche." />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredDomains.map((d) => {
                  const sys = SYSTEM_TYPES[d.systemType] || SYSTEM_TYPES.OTHER;
                  const env = ENVIRONNEMENTS[d.environment] || { label: d.environment, color: '#6B7280' };
                  const SysIcon = sys.icon;
                  return (
                    <div key={d.code} onClick={() => setSelectedCode(d.code)}
                      className="p-5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-500/50 cursor-pointer transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${sys.color}1A`, color: sys.color }}>
                          <SysIcon className="h-5 w-5" />
                        </div>
                        <Badge color={d.active ? '#10B981' : '#6B7280'}>{d.active ? 'Actif' : 'Inactif'}</Badge>
                      </div>
                      <h3 className="font-semibold text-gray-900 dark:text-white text-lg">{d.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 font-mono mt-0.5">{d.code}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <Badge color={sys.color}>{sys.label}</Badge>
                        <Badge color={env.color}>{env.label}</Badge>
                      </div>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-gray-700/60">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><Layers className="h-3 w-3" />{d.modules.length} module(s)</span>
                        <span className="text-xs text-purple-600 dark:text-purple-400 font-medium flex items-center gap-1">Voir dashboard <ArrowRight className="h-3.5 w-3.5" /></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ----- VUE DASHBOARD DU DOMAINE ----- */}
        {domaine && (
          <div className="p-5 space-y-5">
            {/* Rangée KPI : jauge + agrégats + dernière session */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center gap-3">
                <ScoreGauge score={domaine.dashboard.averageConformityScore} size={88} />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Score moyen</p>
                  <p className="text-sm font-semibold mt-1" style={{ color: scoreColor(domaine.dashboard.averageConformityScore) }}>
                    {scoreLevel(domaine.dashboard.averageConformityScore)}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">sessions terminées</p>
                </div>
              </div>
              <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><Activity className="h-3.5 w-3.5" />Sessions</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{domaine.dashboard.totalSessions}</p>
                <p className="text-xs text-gray-400 mt-0.5">total du domaine (agrégat serveur)</p>
              </div>
              <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />Anomalies</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{domaine.dashboard.totalAnomalies}</p>
                <p className="text-xs mt-0.5">
                  <span className="text-red-500 font-semibold">{domaine.dashboard.anomaliesOuvertes} ouvertes</span>
                  <span className="text-gray-400"> • {domaine.dashboard.critiques} critiques</span>
                </p>
              </div>
              <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 min-w-0">
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Dernière session</p>
                <p className="text-xs font-mono font-semibold text-gray-900 dark:text-white mt-1.5 truncate" title={domaine.dashboard.lastSessionCode}>
                  {domaine.dashboard.lastSessionCode}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {domaine.dashboard.lastSessionDate}
                  <span className="ml-1.5 font-bold" style={{ color: scoreColor(domaine.dashboard.lastConformityScore) }}>
                    {domaine.dashboard.lastConformityScore}%
                  </span>
                </p>
              </div>
            </div>

            {/* Tendance des scores */}
            <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <TrendingUp className="h-3.5 w-3.5" />Tendance des scores de conformité (dernières sessions)
              </h4>
              <TendanceChart points={domaine.tendance} />
              <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                <Info className="h-3 w-3" />Points colorés par seuils (vert ≥ 85, ambre ≥ 70, rouge sinon) ; lignes pointillées = seuils 70 et 85.
              </p>
            </div>

            {/* Performance par module + liste des modules */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <BarChart3 className="h-3.5 w-3.5" />Performance par module
                </h4>
                <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                  {modulesTries.map((m) => (
                    <div key={m.module}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium text-gray-900 dark:text-white">{m.module}</span>
                        <span className="font-bold" style={{ color: scoreColor(m.scoreMoyen) }}>{m.scoreMoyen.toFixed(1)}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${m.scoreMoyen}%`, backgroundColor: scoreColor(m.scoreMoyen) }} />
                      </div>
                      <div className="flex gap-3 mt-0.5 text-[10px] text-gray-400">
                        <span>{m.totalSessions} session(s)</span>
                        <span>{m.totalAnomalies} anomalie(s)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Layers className="h-3.5 w-3.5" />Modules audités ({domaine.modules.length})
                </h4>
                <div className="space-y-1">
                  {domaine.modules.map((m) => (
                    <div key={m.module} onClick={() => notify(`Navigation vers les sessions du module ${m.module} (simulation).`)}
                      className="flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: scoreColor(m.scoreMoyen) }} />
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{m.module}</span>
                        {m.ouvertes > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 font-semibold shrink-0">{m.ouvertes} ouv.</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 shrink-0">
                        <span className="hidden sm:inline">{m.derniereSession}</span>
                        <span className="font-semibold" style={{ color: scoreColor(m.scoreMoyen) }}>{m.scoreMoyen.toFixed(0)}%</span>
                        <ArrowRight className="h-3.5 w-3.5 text-gray-300 dark:text-gray-600" />
                      </div>
                    </div>
                  ))}
                </div>
                {domaine.dashboard.anomaliesOuvertes === 0 && (
                  <div className="text-center py-3 mt-2 border-t border-gray-100 dark:border-gray-700/60">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">Aucune anomalie ouverte sur ce domaine</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Toast simulé */}
        {toast && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <Info className="h-4 w-4 text-purple-500" />{toast}
          </div>
        )}
      </Card>

      {/* ===== (3) Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Search className="h-5 w-5 text-blue-500" />1. Choisir un domaine</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Recherchez par <strong>nom</strong>, <strong>code</strong> ou <strong>type de système</strong> (CBS, AML…).</GuideStep>
              <GuideStep n={2}>Chaque carte affiche l'<strong>environnement</strong>, le <strong>statut actif</strong> et le nombre de <strong>modules</strong>.</GuideStep>
              <GuideStep n={3}>Cliquez sur une carte pour ouvrir le dashboard. <button onClick={() => setSelectedCode('CBS_AMPLITUDE')} className="text-blue-600 dark:text-blue-400 underline">Essayer (CBS_AMPLITUDE)</button></GuideStep>
            </ol>
          </CardContent></Card>
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Target className="h-5 w-5 text-blue-500" />2. Lire les agrégats</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>La jauge donne le <strong>score moyen</strong> du domaine (agrégat serveur <code className="text-xs">averageConformityScore</code>).</GuideStep>
              <GuideStep n={2}>Les tuiles totalisent <strong>sessions</strong> et <strong>anomalies</strong> (dont ouvertes / critiques) ; la <strong>dernière session</strong> est rappelée avec son score.</GuideStep>
              <GuideStep n={3}>La <strong>tendance</strong> situe les dernières sessions par rapport aux seuils 70 / 85. <button onClick={() => setSelectedCode('AML_TRACKER')} className="text-blue-600 dark:text-blue-400 underline">Voir un domaine critique</button></GuideStep>
            </ol>
          </CardContent></Card>
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Layers className="h-5 w-5 text-blue-500" />3. Analyser par module</h3>
            <ol className="space-y-2">
              <GuideStep n={1}><strong>Performance par module</strong> classe les modules CBS du meilleur au moins bon score.</GuideStep>
              <GuideStep n={2}>La liste <strong>Modules audités</strong> rappelle la dernière session et le nombre d'anomalies ouvertes de chaque module.</GuideStep>
              <GuideStep n={3}>Un clic sur un module ouvre les <strong>sessions filtrées</strong> (domaine + module) dans le Suivi d'audit.</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== (4) Règles de gestion ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Calculator className="h-5 w-5 text-blue-500" />Règles de gestion</h3>
        <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 mb-4">
          <p className="text-sm font-mono text-gray-800 dark:text-gray-100">Score = 100 − (CRITICAL×10 + MAJOR×5 + MINOR×1) ÷ NbContrôles × 10</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Seuils : <strong style={{ color: '#EF4444' }}>CRITIQUE &lt; 70</strong> • <strong style={{ color: '#F59E0B' }}>ALERTE &lt; 85</strong> • <strong style={{ color: '#10B981' }}>ACCEPTABLE ≥ 85</strong>.
            Le score du domaine et celui de chaque module sont des <strong>moyennes des scores de sessions</strong> calculées côté serveur.
          </p>
        </div>
        <ul className="space-y-1.5 text-sm text-gray-600 dark:text-gray-300 list-disc list-inside">
          <li>Les KPI de tête viennent de <code>/domains/{'{code}'}/dashboard</code> ; les répartitions par module viennent de l'agrégat <code>/supervision</code> (le domaine y est extrait par son code).</li>
          <li>Les sessions récentes sont chargées par <strong>recherche paginée bornée</strong> (10 dernières) — la table complète n'est jamais téléchargée.</li>
          <li>Chaque appel a son propre repli : en cas d'échec partiel, les tuiles concernées restent à zéro sans bloquer la page.</li>
          <li>Les modules affichés proviennent du référentiel AuditModule du domaine (origine DECLARE ou DECOUVERT à l'ingestion).</li>
        </ul>
      </CardContent></Card>

      {/* ===== (5) Endpoints ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><ListChecks className="h-5 w-5 text-blue-500" />Endpoints API</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>{['Méthode', 'Endpoint', 'Usage', 'Permission'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {[
                ['/api/scanbank/reports/domains/{domainCode}/dashboard', 'Agrégats du domaine (sessions, score moyen, anomalies, dernière session)', 'SCANBANK_REPORT_VIEW'],
                ['/api/scanbank/reports/supervision', 'Répartitions par module (sévérité, catégorie, ouvertes)', 'SCANBANK_REPORT_VIEW'],
                ['/api/scanbank/audit/domains', 'Référentiel des domaines (sélecteur)', 'SCANBANK_AUDIT_VIEW'],
                ['/api/scanbank/audit/domains/{code}', 'Fiche du domaine sélectionné', 'SCANBANK_AUDIT_VIEW'],
                ['/api/scanbank/audit/sessions/page?domainCode={code}&size=10&sort=id,desc', 'Sessions récentes (page bornée)', 'SCANBANK_AUDIT_VIEW'],
              ].map((r, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2"><Badge color="#10B981">GET</Badge></td>
                  <td className="px-4 py-2 font-mono text-xs break-all">{r[0]}</td>
                  <td className="px-4 py-2 text-xs">{r[1]}</td>
                  <td className="px-4 py-2 font-mono text-[11px]">{r[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
          <ArrowRight className="h-3 w-3" />Source : écran réel <code>[RPT][02]-DomainDashboard.js</code> + ReportController (scanbank_core).
        </p>
      </CardContent></Card>
    </div>
  );
};

export default DomainDashboardPOC;
